// frontend/src/components/shared/modals/HierarchicalProgramModal.tsx

import { memo, useState, useEffect } from 'react';
import { Loader2, ChevronDown, ChevronRight, Plus, X, Info, AlertTriangle } from 'lucide-react';
import { ModalShell } from '../../ui';

import { useAuth } from '../../../contexts/AuthContext';
import { typedApi } from '../../../services/typedApiClient';
import { isGenEdProgram as isGenEdProgramShared, isUniversityRequirementsProgram } from '@/utils/programClassification';

interface ProgramShape {
  program_name?: string;
  program?: string;
  name?: string;
  type?: string;
  degree_type?: string;
  track?: string;
  track_name?: string;
}

interface CurrentProgram {
  program: string;
  track: string;
  type: string;
  program_name?: string;
  degree_type?: string;
  /** The plan entry as stored. Its keys (e.g. concentration_department) go back unchanged. */
  raw?: Record<string, unknown>;
}

type PlanEntry = { program?: string; module?: string; name?: string; track?: string };

/** The plan entry to send back: every stored key passes through, with the display's program and track.
 * Rebuilding {program, track} silently dropped keys the backend relies on, e.g. UMD CS's
 * concentration_department on every save. */
const toPayloadEntry = (p: CurrentProgram): { program: string; track: string } & Record<string, unknown> => ({
  ...(p.raw ?? {}),
  program: p.program,
  track: p.track,
});

interface ApiProgram {
  id: string | number;
  name: string;
  program_name: string;
  program_code?: string;
  track_name?: string;
  type?: string;
  degree_type?: string;
  credits_required?: number;
  [key: string]: unknown;
}

interface ApiProgramGroup {
  id: string;
  name: string;
  children: ApiProgram[];
  [key: string]: unknown;
}

interface CurrentAcademicPlanShape {
  majors?: Array<string | { program?: string; module?: string; name?: string; track?: string }>;
  minors?: Array<string | { program?: string; module?: string; name?: string; track?: string }>;
  citations?: Array<string | { program?: string; module?: string; name?: string; track?: string }>;
  // Prior-stack Phase 5: pre-health advising overlays (warning-tier).
  advising_overlays?: Array<string | { program?: string; module?: string; name?: string; track?: string }>;
  [key: string]: unknown;
}

interface LtscConfirmState {
  program: ApiProgram;
  message: string;
  onConfirm: () => void;
}

export interface HierarchicalProgramModalProps {
  isOpen: boolean;
  onClose: () => void;
  hierarchicalData?: unknown;
  academicPlan: { id?: number } | null | undefined;
  onProgramsUpdated?: () => Promise<void> | void;
}

// One predicate for the whole UI (utils/programClassification): the
// degree_type === 'GE' / type === 'general_education' legs are
// institution-agnostic; the name legs cover plan-state entries.
const isGenEdProgram = (program: ProgramShape | null | undefined): boolean =>
  isGenEdProgramShared(program);

const isAutoManagedProgram = (program: ProgramShape | null | undefined): boolean => {
  if (!program) return false;
  if (isGenEdProgram(program)) return true;
  const name = program.program_name || program.program || program.name || '';
  return (
    name === 'University Requirements' ||
    program.type === 'university_requirements' ||
    program.degree_type === 'UR'
  );
};

/**
 * Hierarchical Program Management Modal.
 *
 * Tree-style program selection that eliminates the artificial major/minor
 * separation. Gen Ed is auto-managed by the backend (added with first major,
 * removed when last major is removed; cannot be manually toggled).
 */
const HierarchicalProgramModal = memo(
  ({ isOpen, onClose, academicPlan, onProgramsUpdated }: HierarchicalProgramModalProps) => {
    const { user, isAuthenticated } = useAuth();

    const [searchTerm, setSearchTerm] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');
    const [programGroups, setProgramGroups] = useState<ApiProgramGroup[]>([]);
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
      new Set(['computer_science', 'mathematics'])
    );
    const [loading, setLoading] = useState(false);
    const [updating, setUpdating] = useState(false);
    // Which single row is mutating, e.g. 'remove:Computer Science|General
    // Track' or 'add:42'. `updating` correctly disables ALL buttons during a
    // mutation, but the busy LABEL must only appear on the clicked row —
    // removing one of two programs used to show "Removing..." on both
    // (UC Merced QA, 2026-08).
    const [busyKey, setBusyKey] = useState<string | null>(null);
    const [error, setError] = useState('');
    const [ltscRemovedMessage, setLtscRemovedMessage] = useState('');
    const [ltscConfirm, setLtscConfirm] = useState<LtscConfirmState | null>(null);

    const [currentAcademicPlan, setCurrentAcademicPlan] = useState<CurrentAcademicPlanShape | null>(
      null
    );


    useEffect(() => {
      if (!isOpen || !user?.id) return;

      const fetchData = async (): Promise<void> => {
        try {
          setLoading(true);
          setError('');

          const programsData = await typedApi.programs.getHierarchical();
          setProgramGroups(((programsData as { programs?: ApiProgramGroup[] }).programs ||
            []) as ApiProgramGroup[]);

          if (academicPlan?.id) {
            const planData = await typedApi.academicPlans.get(academicPlan.id);
            setCurrentAcademicPlan(
              (planData as { plan?: CurrentAcademicPlanShape }).plan ?? null
            );
          }
        } catch (err) {
          console.error('Error fetching data:', err);
          setError((err as { message?: string })?.message ?? 'Failed to load programs');
        } finally {
          setLoading(false);
        }
      };

      fetchData();
    }, [isOpen, academicPlan?.id, user?.id]);

    useEffect(() => {
      if (!isOpen || !user?.id) {
        setCurrentAcademicPlan(null);
      }
    }, [isOpen, user?.id]);

    const getCurrentPrograms = (): CurrentProgram[] => {
      if (!currentAcademicPlan) {
        return [];
      }

      const majors: CurrentProgram[] = (currentAcademicPlan.majors || []).map((major) => {
        if (typeof major === 'string') {
          return { program: major, track: 'General Track', type: 'major' };
        }
        const program = major.program || major.module || major.name || String(major);
        const track = major.track || 'General Track';
        const obj: CurrentProgram = {
          program, track, type: 'major', program_name: program, raw: { ...(major as PlanEntry) },
        };
        if (isGenEdProgram(obj)) obj.type = 'general_education';
        else if (isAutoManagedProgram(obj)) obj.type = 'university_requirements';
        return obj;
      });

      const minors: CurrentProgram[] = (currentAcademicPlan.minors || []).map((minor) => {
        if (typeof minor === 'string') {
          return { program: minor, track: 'General Track', type: 'minor' };
        }
        const program = minor.program || minor.module || minor.name || String(minor);
        const track = minor.track || 'General Track';
        return { program, track, type: 'minor', raw: { ...(minor as PlanEntry) } };
      });

      const citations: CurrentProgram[] = (currentAcademicPlan.citations || []).map((citation) => {
        if (typeof citation === 'string') {
          return { program: citation, track: 'General Track', type: 'citation' };
        }
        const program = citation.program || citation.module || citation.name || String(citation);
        const track = citation.track || 'General Track';
        return { program, track, type: 'citation', raw: { ...(citation as PlanEntry) } };
      });

      // Prior-stack Phase 5: pre-health advising overlays (warning-tier).
      const advisingOverlays: CurrentProgram[] = (currentAcademicPlan.advising_overlays || []).map(
        (overlay) => {
          if (typeof overlay === 'string') {
            return { program: overlay, track: 'General', type: 'advising' };
          }
          const program = overlay.program || overlay.module || overlay.name || String(overlay);
          const track = overlay.track || 'General';
          return { program, track, type: 'advising', raw: { ...(overlay as PlanEntry) } };
        }
      );

      return [...majors, ...minors, ...citations, ...advisingOverlays];
    };

    const currentPrograms = getCurrentPrograms();

    const isProgramSelected = (program: ApiProgram): boolean => {
      return currentPrograms.some(
        (current) => current.program === program.program_name && current.track === program.track_name
      );
    };

    interface TypeInfo {
      bgClass: string;
      textClass: string;
      badgeBgClass: string;
      badgeTextClass: string;
      label: string;
    }

    const getProgramTypeInfo = (program: ProgramShape): TypeInfo => {
      const isGenEd = isGenEdProgramShared(program);
      const isUnivReq = isUniversityRequirementsProgram(program);
      const isExploratory = program.degree_type === 'Exploratory' || program.type === 'exploratory';

      // Tier 3.5c: role grammar from the audit column — low-alpha wash +
      // tinted, dark-aware badge; same hues as the role dots/stripes.
      if (isGenEd) {
        return {
          bgClass: 'bg-role-gened/10',
          textClass: 'text-ink',
          badgeBgClass: 'bg-role-gened/12',
          badgeTextClass: 'text-green-700 dark:text-green-300',
          label: 'Gen Ed',
        };
      } else if (isUnivReq) {
        return {
          bgClass: 'bg-surface-sunken/70',
          textClass: 'text-ink',
          badgeBgClass: 'bg-surface-sunken',
          badgeTextClass: 'text-ink-2',
          label: 'University',
        };
      } else if (program.type === 'minor' || program.degree_type === 'Minor') {
        return {
          bgClass: 'bg-role-minor/10',
          textClass: 'text-ink',
          badgeBgClass: 'bg-role-minor/10',
          badgeTextClass: 'text-rose-700 dark:text-rose-300',
          label: 'Minor',
        };
      } else if (program.type === 'citation' || program.degree_type === 'Citation') {
        return {
          bgClass: 'bg-role-citation/10',
          textClass: 'text-ink',
          badgeBgClass: 'bg-role-citation/15',
          badgeTextClass: 'text-amber-800 dark:text-amber-300',
          label: 'Citation',
        };
      } else if (program.type === 'advising' || program.degree_type === 'ADVISING') {
        // Prior-stack Phase 5: pre-health advising overlay (warning-tier).
        return {
          bgClass: 'bg-role-advising/10',
          textClass: 'text-ink',
          badgeBgClass: 'bg-role-advising/15',
          badgeTextClass: 'text-violet-700 dark:text-violet-300',
          label: 'Pre-Health',
        };
      } else if (isExploratory) {
        return {
          bgClass: 'bg-role-major/10',
          textClass: 'text-ink',
          badgeBgClass: 'bg-role-major/10',
          badgeTextClass: 'text-blue-700 dark:text-blue-300',
          label: 'Exploratory',
        };
      } else {
        return {
          bgClass: 'bg-role-major/10',
          textClass: 'text-ink',
          badgeBgClass: 'bg-role-major/10',
          badgeTextClass: 'text-blue-700 dark:text-blue-300',
          label: 'Major',
        };
      }
    };

    const toggleGroup = (groupId: string): void => {
      const newExpanded = new Set(expandedGroups);
      if (newExpanded.has(groupId)) {
        newExpanded.delete(groupId);
      } else {
        newExpanded.add(groupId);
      }
      setExpandedGroups(newExpanded);
    };

    const executeAddProgram = async (
      program: ApiProgram,
      { replaceLtsc = false, replaceWithLtsc = false }: { replaceLtsc?: boolean; replaceWithLtsc?: boolean } = {}
    ): Promise<void> => {
      try {
        setUpdating(true);
        setBusyKey(`add:${program.id}`);
        setError('');

        const programObject = {
          program: program.program_name,
          track: program.track_name ?? 'General Track',
        };

        const currentMajors = currentPrograms.filter(
          (p) => p.type === 'major' && !isAutoManagedProgram(p)
        );
        const currentMinors = currentPrograms.filter((p) => p.type === 'minor');
        const currentCitations = currentPrograms.filter((p) => p.type === 'citation');
        const currentAdvising = currentPrograms.filter((p) => p.type === 'advising');

        const isMinor = program.degree_type === 'Minor';
        const isCitation = program.degree_type === 'Citation';
        const isAdvising = program.degree_type === 'ADVISING' || program.type === 'advising';
        const isMajor = !isMinor && !isCitation && !isAdvising;

        let updatedMajors;
        if (replaceWithLtsc) {
          updatedMajors = [programObject];
        } else if (isMajor) {
          updatedMajors = [
            ...currentMajors.map(toPayloadEntry),
            programObject,
          ];
        } else {
          updatedMajors = currentMajors.map(toPayloadEntry);
        }

        const updatedMinors = isMinor
          ? [...currentMinors.map(toPayloadEntry), programObject]
          : currentMinors.map(toPayloadEntry);
        const updatedCitations = isCitation
          ? [
              ...currentCitations.map(toPayloadEntry),
              programObject,
            ]
          : currentCitations.map(toPayloadEntry);
        const updatedAdvising = isAdvising
          ? [...currentAdvising.map(toPayloadEntry), programObject]
          : currentAdvising.map(toPayloadEntry);

        if (!academicPlan?.id) return;

        await typedApi.academicPlans.updatePrograms(academicPlan.id, {
          majors: updatedMajors,
          minors: updatedMinors,
          citations: updatedCitations,
          advising_overlays: updatedAdvising,
        });

        if (academicPlan?.id && user?.id) {
          try {
            const planData = await typedApi.academicPlans.get(academicPlan.id);
            setCurrentAcademicPlan(
              (planData as { plan?: CurrentAcademicPlanShape }).plan ?? null
            );

            if (replaceLtsc) {
              setLtscRemovedMessage(
                `Letters and Sciences removed — you've declared ${program.program_name}!`
              );
              setTimeout(() => setLtscRemovedMessage(''), 5000);
            } else if (replaceWithLtsc) {
              setLtscRemovedMessage('All majors replaced with Letters and Sciences (Exploratory)');
              setTimeout(() => setLtscRemovedMessage(''), 5000);
            }
          } catch (refreshError) {
            console.warn('Failed to refresh academic plan data:', refreshError);
          }
        }

        if (onProgramsUpdated) {
          onProgramsUpdated();
        }
      } catch (err) {
        console.error('Error adding program:', err);
        setError((err as { message?: string })?.message ?? 'Failed to add program');
      } finally {
        setUpdating(false);
        setBusyKey(null);
      }
    };

    const handleAddProgram = async (program: ApiProgram): Promise<void> => {
      if (!academicPlan?.id) {
        setError('No academic plan selected');
        return;
      }
      if (!isAuthenticated || !user?.id) {
        setError('User not authenticated. Please log in and try again.');
        return;
      }
      if (isAutoManagedProgram(program)) {
        return;
      }

      const isMinor = program.degree_type === 'Minor';
      const isCitation = program.degree_type === 'Citation';
      const isAdvising = program.degree_type === 'ADVISING' || program.type === 'advising';
      const isMajor = !isMinor && !isCitation && !isAdvising;
      const isLtsc =
        program.program_name === 'Letters and Sciences' && program.degree_type === 'Exploratory';
      const realMajors = currentPrograms.filter(
        (p) =>
          p.type === 'major' && !isAutoManagedProgram(p) && p.program !== 'Letters and Sciences'
      );
      const hasLtsc = currentPrograms.some((p) => p.program === 'Letters and Sciences');

      if (isMajor && !isLtsc && hasLtsc) {
        setLtscConfirm({
          program,
          message: `Adding ${program.program_name} will replace Letters and Sciences (Exploratory). Do you want to continue?`,
          onConfirm: () => {
            setLtscConfirm(null);
            executeAddProgram(program, { replaceLtsc: true });
          },
        });
        return;
      }

      if (isLtsc && realMajors.length > 0) {
        const majorNames = realMajors.map((m) => m.program).join(', ');
        setLtscConfirm({
          program,
          message: `Adding Letters and Sciences (Exploratory) will remove all your current majors (${majorNames}). Do you want to continue?`,
          onConfirm: () => {
            setLtscConfirm(null);
            executeAddProgram(program, { replaceWithLtsc: true });
          },
        });
        return;
      }

      await executeAddProgram(program);
    };

    const handleRemoveProgram = async (programToRemove: CurrentProgram): Promise<void> => {
      if (!academicPlan?.id) {
        setError('No academic plan selected');
        return;
      }

      if (!isAuthenticated || !user?.id) {
        setError('User not authenticated. Please log in and try again.');
        return;
      }

      if (isAutoManagedProgram(programToRemove)) {
        return;
      }

      try {
        setUpdating(true);
        setBusyKey(`remove:${programToRemove.program}|${programToRemove.track ?? ''}`);
        setError('');

        const isMatch = (p: CurrentProgram): boolean =>
          p.program === programToRemove.program && p.track === programToRemove.track;

        const remainingMajors = currentPrograms
          .filter((p) => p.type === 'major')
          .filter((p) => !isGenEdProgram(p))
          .filter((p) => !isMatch(p))
          .map(toPayloadEntry);

        const updatedMinors = currentPrograms
          .filter((p) => p.type === 'minor')
          .filter((p) => !isMatch(p))
          .map(toPayloadEntry);

        const updatedCitations = currentPrograms
          .filter((p) => p.type === 'citation')
          .filter((p) => !isMatch(p))
          .map(toPayloadEntry);

        const updatedAdvising = currentPrograms
          .filter((p) => p.type === 'advising')
          .filter((p) => !isMatch(p))
          .map(toPayloadEntry);

        await typedApi.academicPlans.updatePrograms(academicPlan.id, {
          majors: remainingMajors,
          minors: updatedMinors,
          citations: updatedCitations,
          advising_overlays: updatedAdvising,
        });

        if (academicPlan?.id && user?.id) {
          try {
            const planData = await typedApi.academicPlans.get(academicPlan.id);
            setCurrentAcademicPlan(
              (planData as { plan?: CurrentAcademicPlanShape }).plan ?? null
            );
          } catch (refreshError) {
            console.warn('Failed to refresh academic plan data:', refreshError);
          }
        }

        if (onProgramsUpdated) {
          onProgramsUpdated();
        }
      } catch (err) {
        console.error('Error removing program:', err);
        setError((err as { message?: string })?.message ?? 'Failed to remove program');
      } finally {
        setUpdating(false);
        setBusyKey(null);
      }
    };

    const filteredGroups = programGroups
      .map((group) => ({
        ...group,
        children: group.children.filter(
          (program) =>
            !isAutoManagedProgram(program) &&
            (typeFilter === 'all' || program.type === typeFilter) &&
            (program.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
              program.program_name.toLowerCase().includes(searchTerm.toLowerCase()))
        ),
      }))
      .filter((group) => group.children.length > 0);

    if (!isOpen) return null;

    return (
      <ModalShell
        open={isOpen}
        onClose={onClose}
        ariaLabel="Manage academic programs"
        isLoading={updating}
        maxWidth="max-w-[calc(100vw-1rem)] sm:max-w-3xl"
        className="max-h-[85vh] overflow-hidden"
      >
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Manage Academic Programs</h2>
              <button
                onClick={onClose}
                disabled={updating}
                className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
            {error && (
              <div className="mb-4 p-3 bg-status-critical/10 border border-status-critical/25 rounded-card">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {ltscRemovedMessage && (
              <div className="mb-4 p-3 bg-status-progress/10 border border-status-progress/25 rounded-card">
                <p className="text-sm text-blue-700">{ltscRemovedMessage}</p>
              </div>
            )}

            {ltscConfirm && (
              <div className="mb-4 p-4 bg-status-warning/10 border border-status-warning/25 rounded-card">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm text-amber-800 font-medium mb-3">{ltscConfirm.message}</p>
                    <div className="flex gap-2">
                      <button
                        onClick={ltscConfirm.onConfirm}
                        className="px-3 py-1.5 text-sm font-medium text-status-warning-fg bg-status-warning/15 border border-status-warning/30 rounded-control hover:bg-status-warning/25 transition-colors"
                      >
                        Continue
                      </button>
                      <button
                        onClick={() => setLtscConfirm(null)}
                        className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Current Programs</h3>
              <div className="space-y-2">
                {currentPrograms
                  .filter((p) => p.type !== 'university_requirements')
                  .map((program, index) => {
                    const isAutoManaged = isAutoManagedProgram(program);
                    const typeInfo = getProgramTypeInfo(program);

                    return (
                      <div
                        key={`current-${index}`}
                        className={`flex items-center justify-between p-3 rounded-lg ${typeInfo.bgClass}`}
                      >
                        <div className="flex-1">
                          <span className={`font-medium ${typeInfo.textClass}`}>
                            {program.program}
                            {program.track && program.track !== 'General Track'
                              ? ` - ${program.track}`
                              : ''}
                          </span>
                          <span
                            className={`ml-2 px-2 py-0.5 text-xs rounded-full ${typeInfo.badgeBgClass} ${typeInfo.badgeTextClass}`}
                          >
                            {typeInfo.label}
                          </span>
                        </div>
                        {isAutoManaged ? (
                          <div className="flex items-center text-gray-500 text-sm">
                            <Info className="w-4 h-4 mr-1" />
                            <span className="text-xs">Auto-managed</span>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleRemoveProgram(program)}
                            disabled={updating}
                            className="text-red-600 hover:text-red-800 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {busyKey === `remove:${program.program}|${program.track ?? ''}`
                              ? 'Removing...'
                              : 'Remove'}
                          </button>
                        )}
                      </div>
                    );
                  })}

                {currentPrograms.length === 0 && (
                  <p className="text-gray-500 text-center py-4">No programs selected yet</p>
                )}
              </div>

              {currentPrograms.some((p) => isGenEdProgram(p)) && (
                <div className="mt-3 p-3 bg-success/10 border border-success/25 rounded-card">
                  <div className="flex items-start">
                    <Info className="w-4 h-4 text-success mt-0.5 mr-2 flex-shrink-0" />
                    <p className="text-sm text-ink-2">
                      General Education requirements are automatically included with your major(s)
                      and will be removed if you remove all majors.
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="mb-4">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Add Programs</h3>
              <div className="flex flex-wrap gap-2 mb-3">
                {[
                  { value: 'all', label: 'All', activeClass: 'bg-gray-800 text-white' },
                  {
                    value: 'major',
                    label: 'Majors',
                    activeClass: 'bg-blue-500/15 text-blue-700 dark:text-blue-300 ring-1 ring-blue-500/40',
                  },
                  {
                    value: 'minor',
                    label: 'Minors',
                    activeClass: 'bg-rose-500/15 text-rose-700 dark:text-rose-300 ring-1 ring-rose-500/40',
                  },
                  {
                    value: 'citation',
                    label: 'Citations',
                    activeClass: 'bg-amber-500/15 text-amber-800 dark:text-amber-300 ring-1 ring-amber-500/40',
                  },
                ].map(({ value, label, activeClass }) => (
                  <button
                    key={value}
                    onClick={() => setTypeFilter(value)}
                    className={`px-3 py-1 text-sm rounded-full transition-colors ${
                      typeFilter === value ? activeClass : 'bg-surface-sunken text-ink-2 hover:bg-line-soft/60'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <input
                type="text"
                placeholder="Search for programs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                disabled={loading || updating}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              />
            </div>

            <div className="space-y-3">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                  <span className="ml-2 text-gray-600">Loading programs...</span>
                </div>
              ) : filteredGroups.length > 0 ? (
                filteredGroups.map((group) => (
                  <div key={group.id} className="border border-gray-200 rounded-lg">
                    <button
                      onClick={() => toggleGroup(group.id)}
                      className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center">
                        {expandedGroups.has(group.id) ? (
                          <ChevronDown className="w-5 h-5 text-gray-500 mr-2" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-gray-500 mr-2" />
                        )}
                        <h4 className="text-lg font-medium text-gray-900 break-words">
                          {group.name}
                        </h4>
                      </div>
                      <span className="text-sm text-gray-500">
                        {group.children.length} program{group.children.length !== 1 ? 's' : ''}
                      </span>
                    </button>

                    {expandedGroups.has(group.id) && (
                      <div className="border-t border-gray-200 divide-y divide-gray-100">
                        {group.children.map((program) => {
                          const isSelected = isProgramSelected(program);
                          const typeInfo = getProgramTypeInfo(program);

                          return (
                            <div key={program.id} className="p-4 hover:bg-gray-50">
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center">
                                    <h5 className="font-medium text-gray-900 break-words">
                                      {program.name}
                                    </h5>
                                    <span
                                      className={`ml-2 px-2 py-0.5 text-xs rounded-full ${typeInfo.badgeBgClass} ${typeInfo.badgeTextClass}`}
                                    >
                                      {typeInfo.label}
                                    </span>
                                  </div>
                                  <p className="text-sm text-gray-600 mt-1">
                                    {program.program_code} • {program.credits_required} credits
                                  </p>
                                </div>
                                <button
                                  onClick={() => handleAddProgram(program)}
                                  disabled={updating || isSelected}
                                  className={`ml-4 px-3 py-1.5 text-sm rounded-md transition-colors flex items-center ${
                                    isSelected
                                      ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                      : updating
                                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                        : 'bg-primary text-white hover:bg-primary-800'
                                  }`}
                                >
                                  {isSelected ? (
                                    'Added'
                                  ) : busyKey === `add:${program.id}` ? (
                                    'Adding...'
                                  ) : (
                                    <>
                                      <Plus className="w-4 h-4 mr-1" />
                                      Add
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-8">
                  {searchTerm || typeFilter !== 'all'
                    ? `No ${typeFilter !== 'all' ? typeFilter : ''} programs found${searchTerm ? ` matching "${searchTerm}"` : ''}`
                    : 'No programs available'}
                </p>
              )}
            </div>
          </div>

          <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
            <button
              onClick={onClose}
              disabled={updating}
              className="px-4 py-2 text-ink bg-surface-sunken rounded-control hover:bg-line-soft/60 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {updating ? 'Updating...' : 'Done'}
            </button>
          </div>
      </ModalShell>
    );
  }
);

HierarchicalProgramModal.displayName = 'HierarchicalProgramModal';

export default HierarchicalProgramModal;
